import React, { Component, useEffect } from 'react'
import { Table, Tr, Th, Td } from 'Components/Stats/Table'
import { renderCellData } from './util'
import './../types'
import injectStyles from 'react-jss'
import AnimatedArrow from 'Components/Icons/AnimatedArrow'
import classNames from 'classnames'
import { copyStringToClipboard } from './../../../Utils/Elements'

const styles = {
  table: {
    minWidth: '100%',
  },
  header: {
    borderBottom: '1px solid #fcfcfc',
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
  },
  headerTh: {
    cursor: 'pointer',
  },
  tbodyWrapper: {
    width: '100%',
    display: 'block',
    overflowY: 'scroll',
    overflowX: 'hidden',
    tableLayout: 'fixed',
    maxHeight: '1200px',
  },
  tr: {
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
  },
  footer: {
    borderTop: '5px solid #f8f8f8',
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
  },
  sortArrow: {
    marginRight: 5,
  },
}

/**
 * @typedef ResultsTableProps
 * @property {Array<Object<number|string>>} data This is a hashmap where the key matches
 * the key in the legend. The property has to be a value
 * @property {Array<StatsLegend>} legend
 * @property {object} totals The same format as data objects but to show totals
 * @property {object} sortIndicators The key should be the legend key and
 * have a value of up or down to display an indicator
 *
 * @param {ResultsTableProps} props
 */
const UnstyledResultsTable = ({
  data,
  legend,
  totals,
  onClickHeader,
  classes,
  sortIndicators,
  className,
  currencySign,
}) => {
  let thead = React.createRef(),
    tbody = React.createRef(),
    tfoot = React.createRef()

  useEffect(() => {
    //if (!thead.current || !tbody.current || !tfoot.current) return
    var resizeHeadersAndFooters = () => {
      var theadCells = thead.current.querySelectorAll('th'),
        tbodyCells = tbody.current.querySelectorAll('tr:first-child td'),
        tfootCells = tfoot.current.querySelectorAll('th')

      // First set widths to 0 so that the width is directed by the
      // body cells no the header or footer cells
      tbodyCells.forEach((cell, index) => {
        theadCells[index].style.width = '0'
        tfootCells[index].style.width = '0'
      })

      tbodyCells = tbody.current.querySelectorAll('tr:first-child td')

      tbodyCells.forEach((cell, index) => {
        theadCells[index].style.width = cell.clientWidth - 0 + 'px'
        tfootCells[index].style.width = cell.clientWidth - 0 + 'px'
      })
    }
    window.addEventListener('resize', resizeHeadersAndFooters)
    resizeHeadersAndFooters()

    return function cleanup() {
      window.removeEventListener('resize', resizeHeadersAndFooters)
    }
  })
  return (
    <Table className={classNames(classes.table, className)}>
      <thead ref={thead} className={classes.header}>
        <Tr className={classes.tr}>
          {legend.map(({ label, key }) => (
            <Th
              className={classes.headerTh}
              onClick={onClickHeader.bind(this, key)}
              title={label}
              key={key}
            >
              {sortIndicators[key] ? (
                <AnimatedArrow
                  orientation={sortIndicators[key]}
                  className={classes.sortArrow}
                />
              ) : null}
              <span>{label}</span>
            </Th>
          ))}
        </Tr>
      </thead>
      <tbody ref={tbody} className={classes.tbodyWrapper}>
        {data.map((dto, i) => (
          <Tr className={classes.tr} key={i}>
            {legend.map(({ key, showAsPercent }, j) => (
              <Td key={j}>
                <span>
                  {renderCellData(dto[key], showAsPercent, key, currencySign)}
                </span>
              </Td>
            ))}
          </Tr>
        ))}
      </tbody>
      <tfoot ref={tfoot} className={classes.footer}>
        <Tr className={classes.tr}>
          {totals
            ? legend.map(({ key, showAsPercent }, k) => (
                <Th key={k}>
                  <span>
                    {renderCellData(
                      totals[key],
                      showAsPercent,
                      key,
                      currencySign,
                    )}
                  </span>
                </Th>
              ))
            : null}
        </Tr>
      </tfoot>
    </Table>
  )
}

export const ResultsTable = injectStyles(styles)(UnstyledResultsTable)

export class ResultsTableFilter extends Component {
  state = {
    selectedKey: undefined,
  }

  onClickHeader = key => {
    const { selectedKey, ascending } = this.state
    if (selectedKey == key) {
      // If it's already selected reverse the ordering
      return this.setState({
        selectedKey: key,
        ascending: !ascending,
      })
    }
    this.setState({
      selectedKey: key,
      ascending: true,
    })
  }

  render = () => {
    const { data, legend, children, ...otherProps } = this.props
    const { selectedKey, ascending } = this.state
    return React.cloneElement(children, {
      ...otherProps,
      onClickHeader: this.onClickHeader,
      sortIndicators: {
        [selectedKey]: ascending ? 'down' : 'up',
      },
      legend,
      sortKey: selectedKey,
      sortDirection: ascending ? 'asc' : 'desc',
      data: sortDataByKey(data, selectedKey, !ascending),
    })
  }
}

/**
 * Sort data by key
 */
const sortDataByKey = (data, key, reverse) => {
  return data.slice(0).sort((a, b) => {
    var valA = typeof a[key] === 'object' ? a[key].value : a[key],
      valB = typeof b[key] === 'object' ? b[key].value : b[key]
    if (valA < valB) return reverse ? 1 : -1
    if (valA > valB) return reverse ? -1 : 1
    return 0
  })
}

export default props => (
  <ResultsTableFilter {...props}>
    <ResultsTable />
  </ResultsTableFilter>
)

/**
 * We'll split by tabs
 */
const escapeCsvString = str => String(str).replace(/\t/g, '')

/**
 * Utility function: Copies the table data to the clipboard in tab delimeter
 * format.
 */
export const copyDataToClipboard = (legend, data, opt_totals) => {
  var str = ''
  legend.map(({ label }) => (str += escapeCsvString(label) + '\t'))
  str += '\n'
  data.forEach(dto => {
    legend.forEach(({ key, showAsPercent }) => {
      var val = dto[key]
      if (showAsPercent) val = val * 100 + '%'
      str += escapeCsvString(val) + '\t'
    })
    str += '\n'
  })
  opt_totals &&
    legend.forEach(({ key, showAsPercent }) => {
      var val = renderCellData(opt_totals[key])
      if (showAsPercent) val = val * 100 + '%'
      str += escapeCsvString(val) + '\t'
    })
  copyStringToClipboard(str)
}
